import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FolderTree,
  Plus,
  Search,
  ShieldAlert,
  ChevronRight,
  Eye,
  EyeOff,
} from 'lucide-react'
import { isAxiosError } from 'axios'
import { useActiveOrg } from '../../hooks/useActiveOrg'
import AdminGate from '../../components/organisation/AdminGate'
import { httpStatusOf } from '../../components/organisation/gateStatus'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import {
  getProjects,
  getAccessibleProjects,
  getAccessMatrix,
  explainAccess,
  grantAccess,
  getPermissions,
  getSeats,
  getAnalystProjectVisibility,
  setAnalystProjectVisibility,
} from '../../services/api'
import type { MatrixRow, MatrixCell, ExplainTrace } from '../../services/api'
import { shortDate } from '../../lib/shortDate'

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined
    return data?.error || data?.message || err.message || fallback
  }
  if (err instanceof Error) return err.message
  return fallback
}

// ---------------------------------------------------------------------------
// Local helpers / types
// ---------------------------------------------------------------------------

// The backend returns external_org grants on a resource with extra fields the
// shared Permission interface does not (yet) declare. We read them defensively
// for the cross-organisation panel without mutating the shared type.
interface CrossOrgGrant {
  resource_id: string
  grantee_type?: string
  external_org_id?: string
  // Server resolves a human-readable org name (grant_permission → _org_display_name);
  // prefer it so the panel never shows a raw 'extorg:<uuid>' handle to an admin.
  grantee_display_name?: string
  grantee_email?: string
  permission_level?: string
  allow_download?: boolean
  allow_print?: boolean
  expires_at?: string
}

type GranteeType = 'user' | 'group' | 'external_org'
type GrantLevel = 'view' | 'edit' | 'manager'
type Effect = 'allow' | 'deny'

const LEVEL_LABEL: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manage',
  edit: 'Edit',
  comment: 'Comment',
  view: 'View',
}

function levelLabel(level: string | null | undefined): string {
  if (!level) return '—'
  return LEVEL_LABEL[level] ?? level.charAt(0).toUpperCase() + level.slice(1)
}

// Short label for a resource id used in matrix column headers / pickers.
function shortResourceLabel(id: string, names: Record<string, string>): string {
  return names[id] ?? id
}

// ---------------------------------------------------------------------------
// Matrix cell rendering — mirrors the mockup legend exactly.
// ---------------------------------------------------------------------------

function MatrixCellChip({ cell }: { cell: MatrixCell | undefined }) {
  if (!cell) {
    return <span className="text-gray-300 font-bold">—</span>
  }

  const label = levelLabel(cell.level)
  const isAdminLike =
    cell.source === 'owner' ||
    cell.source === 'org_admin' ||
    cell.source === 'platform_admin'

  // Deny override (red) — takes precedence over level display.
  if (cell.source === 'deny' || (!cell.allowed && cell.source !== 'default_deny')) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold bg-red-100 text-red-700">
        ⛔ Denied
      </span>
    )
  }

  // Not allowed / default deny → muted dash.
  if (!cell.allowed || cell.source === 'default_deny' || !cell.level) {
    return <span className="text-gray-300 font-bold">—</span>
  }

  // Capped by licence — amber lock.
  if (cell.capped_by === 'licence') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold bg-amber-100 text-amber-800">
        {label} 🔒
      </span>
    )
  }

  // Owner / admin → dark chip.
  if (isAdminLike) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold bg-iflow-900 text-white">
        {label}
      </span>
    )
  }

  // Group grant → purple.
  if (cell.source === 'group') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold bg-purple-100 text-purple-700">
        {label}
      </span>
    )
  }

  // Inherited from parent → dashed outline.
  if (cell.source === 'inherited') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold bg-white text-iflow-700 border border-dashed border-iflow-300">
        {label} ↳
      </span>
    )
  }

  // Explicit grant → solid (edit-tier) or soft (view-tier).
  const isEditTier = cell.level === 'edit' || cell.level === 'manager' || cell.level === 'admin'
  return (
    <span
      className={
        isEditTier
          ? 'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold bg-iflow-700 text-white'
          : 'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold bg-iflow-100 text-iflow-700'
      }
    >
      {label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ProjectFolderAccess() {
  const queryClient = useQueryClient()

  const { orgId, orgName, isOrgAdmin, isPlatformAdmin, isPersonalOrg } = useActiveOrg()

  // --- Scope selection (matrix columns) ------------------------------------
  const [selectedScope, setSelectedScope] = useState<string[]>([])
  const [scopeFilter, setScopeFilter] = useState('')

  // --- Panels --------------------------------------------------------------
  const [showExplain, setShowExplain] = useState(false)
  const [showGrant, setShowGrant] = useState(false)

  // --- Analyst visibility editor (headline feature, Fig56) -----------------
  const [visibilityAnalyst, setVisibilityAnalyst] = useState('')

  // --- Explain panel state -------------------------------------------------
  const [explainMember, setExplainMember] = useState('')
  const [explainResource, setExplainResource] = useState('')

  // --- Grant form state ----------------------------------------------------
  const [granteeType, setGranteeType] = useState<GranteeType>('user')
  const [granteeEmail, setGranteeEmail] = useState('')
  const [grantGroupId, setGrantGroupId] = useState('')
  const [grantExternalOrgId, setGrantExternalOrgId] = useState('')
  const [grantResource, setGrantResource] = useState('')
  const [grantLevel, setGrantLevel] = useState<GrantLevel>('view')
  const [grantEffect, setGrantEffect] = useState<Effect>('allow')

  // -------------------------------------------------------------------------
  // Data: candidate resources for the scope picker.
  //   Primary: getProjects() (org's own projects).
  //   Fallback: union of getAccessibleProjects() if the project list is empty.
  // -------------------------------------------------------------------------
  const enabled = Boolean(isOrgAdmin && orgId)

  const projectsQuery = useQuery({
    queryKey: ['projects-for-access', orgId],
    queryFn: getProjects,
    enabled,
  })

  const accessibleQuery = useQuery({
    queryKey: ['accessible-projects', orgId],
    queryFn: () => getAccessibleProjects(),
    enabled: enabled && (projectsQuery.data?.length ?? 0) === 0 && !projectsQuery.isLoading,
  })

  // Candidate resource ids + display names for headers / pickers.
  const { candidateIds, resourceNames } = useMemo(() => {
    const names: Record<string, string> = {}
    const fromProjects = projectsQuery.data ?? []
    if (fromProjects.length > 0) {
      for (const p of fromProjects) names[p.project_id] = p.name
      return { candidateIds: fromProjects.map((p) => p.project_id), resourceNames: names }
    }
    // Fallback: accessible projects — now carry server-resolved names, so the
    // matrix/picker labels by name instead of a raw UUID.
    const accessible = (accessibleQuery.data ?? []).slice(0, 6)
    for (const p of accessible) names[p.project_id] = p.name
    return { candidateIds: accessible.map((p) => p.project_id), resourceNames: names }
  }, [projectsQuery.data, accessibleQuery.data])

  const filteredCandidates = useMemo(() => {
    const f = scopeFilter.trim().toLowerCase()
    if (!f) return candidateIds
    return candidateIds.filter(
      (id) =>
        id.toLowerCase().includes(f) ||
        (resourceNames[id]?.toLowerCase().includes(f) ?? false),
    )
  }, [candidateIds, scopeFilter, resourceNames])

  // -------------------------------------------------------------------------
  // Data: the org's own Analyst-role members, for the Analyst visibility panel.
  // Reuses the same real seats endpoint as Members & Licences.
  // -------------------------------------------------------------------------
  const seatsQuery = useQuery({
    queryKey: ['seats-for-access', orgId],
    queryFn: () => getSeats(orgId as string),
    enabled,
  })
  const analystOptions = useMemo(() => {
    return (seatsQuery.data ?? [])
      .filter(
        (s) => s.state !== 'removed' && /analyst/i.test(s.role_display || s.role || ''),
      )
      .map((s) => ({ id: s.member_id, label: s.member_email || s.member_id }))
  }, [seatsQuery.data])

  // Cap matrix columns at 8 for readability.
  const scopeIds = useMemo(() => selectedScope.slice(0, 8), [selectedScope])

  function toggleScope(id: string) {
    setSelectedScope((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= 8) return prev // hard cap at 8 columns
      return [...prev, id]
    })
  }

  // -------------------------------------------------------------------------
  // Data: the access matrix for the selected scope.
  // -------------------------------------------------------------------------
  const matrixQuery = useQuery({
    queryKey: ['access-matrix', orgId, scopeIds],
    queryFn: () => getAccessMatrix(orgId as string, scopeIds),
    enabled: enabled && scopeIds.length > 0,
  })

  const matrixRows: MatrixRow[] = matrixQuery.data?.rows ?? []
  const memberRows = matrixRows.filter((r) => r.type === 'member')

  // -------------------------------------------------------------------------
  // Data: explain trace.
  // -------------------------------------------------------------------------
  const explainQuery = useQuery({
    queryKey: ['explain-access', explainResource, explainMember],
    queryFn: () => explainAccess(explainResource, explainMember || undefined),
    enabled: showExplain && Boolean(explainResource),
  })

  // -------------------------------------------------------------------------
  // Data: cross-organisation grants (external_org) across the scope.
  // -------------------------------------------------------------------------
  const crossOrgQuery = useQuery({
    queryKey: ['cross-org-grants', orgId, scopeIds],
    queryFn: async () => {
      // BUG-153: previously each getPermissions() rejection was swallowed with
      // `.catch(() => [])`, so an API failure rendered as "no external access" —
      // a dangerous false negative on a security-relevant panel. Track failures
      // instead: surface a hard error when EVERY resource fails, and a
      // partial-failure warning when only some do.
      const settled = await Promise.allSettled(
        scopeIds.map((id) =>
          getPermissions(id).then((perms) => perms as unknown as CrossOrgGrant[]),
        ),
      )
      const grants: CrossOrgGrant[] = []
      let failedCount = 0
      for (const result of settled) {
        if (result.status === 'fulfilled') grants.push(...result.value)
        else failedCount += 1
      }
      if (scopeIds.length > 0 && failedCount === scopeIds.length) {
        throw new Error('Could not load cross-organisation grants.')
      }
      return {
        grants: grants.filter((p) => p.grantee_type === 'external_org'),
        failedCount,
      }
    },
    enabled: enabled && scopeIds.length > 0,
  })

  // -------------------------------------------------------------------------
  // Mutation: grant / deny access.
  // -------------------------------------------------------------------------
  const grantMutation = useMutation({
    mutationFn: () =>
      grantAccess({
        resource_id: grantResource,
        permission_level: grantLevel,
        grantee_type: granteeType,
        effect: grantEffect,
        resource_type: 'project',
        ...(granteeType === 'user' ? { grantee_email: granteeEmail } : {}),
        ...(granteeType === 'group' ? { group_id: grantGroupId } : {}),
        ...(granteeType === 'external_org' ? { external_org_id: grantExternalOrgId } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-matrix', orgId, scopeIds] })
      queryClient.invalidateQueries({ queryKey: ['cross-org-grants', orgId, scopeIds] })
      // Reset entry fields but keep the panel open for repeat grants.
      setGranteeEmail('')
      setGrantGroupId('')
      setGrantExternalOrgId('')
      setShowGrant(false)
    },
  })

  const granteeDisplay =
    granteeType === 'user'
      ? granteeEmail || 'this user'
      : granteeType === 'group'
        ? grantGroupId || 'this group'
        : grantExternalOrgId || 'this external org'

  const grantResourceDisplay = grantResource
    ? shortResourceLabel(grantResource, resourceNames)
    : 'the selected resource'

  // -------------------------------------------------------------------------
  // Access gate.
  // -------------------------------------------------------------------------
  if (!isOrgAdmin || !orgId) {
    return (
      <AdminGate
        area="project and folder access"
        orgName={isPersonalOrg ? undefined : orgName}
        isPlatformAdmin={isPlatformAdmin}
      />
    )
  }

  // A 403 on the org's own data means the role does not cover this organisation:
  // explain it once instead of an "Could not load the access matrix" strip.
  const forbidden = [projectsQuery, matrixQuery].find(
    (q) => q.isError && httpStatusOf(q.error) === 403,
  )
  if (forbidden) {
    return <AdminGate area="project and folder access" orgName={orgName} error={forbidden.error} />
  }

  const grantValid =
    Boolean(grantResource) &&
    ((granteeType === 'user' && granteeEmail.trim()) ||
      (granteeType === 'group' && grantGroupId.trim()) ||
      (granteeType === 'external_org' && grantExternalOrgId.trim()))

  // -------------------------------------------------------------------------
  // Render.
  // -------------------------------------------------------------------------
  return (
    <div className="space-y-6">
      <PageHeader
        title="Project & Folder Access"
        description="Determine which members can reach which projects and folders. Access controls WHAT they can open; the seat controls WHAT they can do."
        helpKey="org-access"
        secondaryActions={
          <button
            type="button"
            onClick={() => setShowExplain((v) => !v)}
            className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            Explain a user&apos;s access
          </button>
        }
        primaryAction={
          <button
            type="button"
            onClick={() => {
              setShowGrant(true)
              if (!grantResource && scopeIds.length > 0) setGrantResource(scopeIds[0])
            }}
            className="bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
          >
            <Plus size={16} /> Grant access
          </button>
        }
      />

      <ContextualTip id="org-access-fgac">
        Access here is fine-grained (FGAC): each person maps to exactly the projects, folders and
        disclosure levels they’re authorised for. A grant at a folder cascades to everything inside
        unless a more specific rule overrides it, and a <b>deny</b> always beats an allow at the same
        level.
      </ContextualTip>

      {/* Analyst visibility — the headline feature (Fig56): pick an Analyst, see
          which projects they can currently reach, and hide/reveal per project.
          Save is ADDITIVE — it diffs against the analyst's real stored hidden set
          (loaded from the server) so it can never silently reopen projects that
          were hidden by another admin. Surfaced prominently, above the matrix. */}
      <AnalystVisibilityPanel
        analystOptions={analystOptions}
        analystsLoading={seatsQuery.isLoading}
        selectedAnalyst={visibilityAnalyst}
        onSelectAnalyst={setVisibilityAnalyst}
        candidateIds={candidateIds}
        resourceNames={resourceNames}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ['access-matrix', orgId, scopeIds] })
        }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Scope selector */}
        <div className="light-card p-4 lg:p-6 h-fit">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">
            <FolderTree size={14} /> Scope
          </div>
          <div className="relative mb-3">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value)}
              placeholder="Find a project…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-iflow-200"
            />
          </div>

          {projectsQuery.isLoading ? (
            <LoadingSkeleton rows={4} type="list" />
          ) : filteredCandidates.length === 0 ? (
            <p className="text-xs text-gray-500 py-4 text-center">
              No projects available to scope.
            </p>
          ) : (
            <ul className="space-y-1 max-h-[420px] overflow-y-auto">
              {filteredCandidates.map((id) => {
                const checked = selectedScope.includes(id)
                const atCap = !checked && selectedScope.length >= 8
                return (
                  <li key={id}>
                    <button
                      onClick={() => toggleScope(id)}
                      disabled={atCap}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-sm text-left ${
                        checked
                          ? 'bg-iflow-50 text-iflow-700 font-medium'
                          : atCap
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      title={atCap ? 'Max 8 columns' : undefined}
                    >
                      <span aria-hidden>📁</span>
                      <span className="truncate flex-1">
                        {resourceNames[id] ?? id}
                      </span>
                      {checked && <ChevronRight size={14} className="text-iflow-400" />}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          <div className="mt-3 rounded-lg bg-iflow-50 border border-iflow-200 px-3 py-2 text-xs text-iflow-900">
            Grant at a folder and it cascades to every project and sub-folder
            inside, unless overridden. Pick up to 8 columns.
          </div>
        </div>

        {/* Matrix */}
        <div className="light-card p-4 lg:p-6 overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-bold text-gray-800">Access matrix</div>
              <div className="text-xs text-gray-500">
                {scopeIds.length > 0
                  ? `${memberRows.length} members across ${scopeIds.length} resource${scopeIds.length === 1 ? '' : 's'}`
                  : 'Select a scope to build the matrix'}
              </div>
            </div>
            {scopeIds.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-iflow-50 text-iflow-700 px-3 py-1 text-xs font-medium">
                Scope: {scopeIds.length} selected
              </span>
            )}
          </div>

          {scopeIds.length === 0 ? (
            <EmptyState
              icon={FolderTree}
              title="No scope selected"
              description="Choose one or more projects or folders from the Scope panel to see who can reach them."
              action={
                filteredCandidates.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => toggleScope(filteredCandidates[0])}
                    className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    Select first project
                  </button>
                ) : undefined
              }
            />
          ) : matrixQuery.isLoading ? (
            <LoadingSkeleton rows={5} type="table" />
          ) : matrixQuery.isError ? (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              Could not load the access matrix. Try a smaller scope or reload.
            </div>
          ) : matrixRows.length === 0 ? (
            <EmptyState
              icon={ShieldAlert}
              title="No members yet"
              description="No members or groups have any access recorded for the selected scope."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="text-left text-[11px] uppercase tracking-wide text-gray-500 font-bold px-3 py-2 border-b-2 border-gray-200 sticky left-0 bg-white">
                      Member / group
                    </th>
                    {scopeIds.map((id) => (
                      <th
                        key={id}
                        className="text-center text-[11px] text-gray-700 font-bold px-2 py-2 border-b-2 border-gray-200 whitespace-nowrap"
                      >
                        📁 {resourceNames[id] ?? id}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixRows.map((row, idx) => {
                    const isGroup = row.type === 'group'
                    const key = isGroup
                      ? `group-${row.group_id ?? idx}`
                      : `member-${row.member_id ?? idx}`
                    return (
                      <tr key={key} className={isGroup ? 'bg-purple-50/60' : ''}>
                        <td className="text-left px-3 py-2 border-b border-gray-100 sticky left-0 bg-inherit">
                          {isGroup ? (
                            <div>
                              <div className="font-semibold text-gray-800 flex items-center gap-1.5">
                                <span aria-hidden>👥</span>
                                {row.name ?? row.group_id}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                group · {row.member_count ?? 0} members
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="font-semibold text-gray-800">
                                {row.email ?? row.member_id}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {row.role_display ?? row.role ?? 'Member'}
                                {row.licence_class ? ` · ${row.licence_class}` : ''}
                              </div>
                            </div>
                          )}
                        </td>
                        {scopeIds.map((id) => (
                          <td
                            key={id}
                            className="text-center px-2 py-2 border-b border-gray-100"
                          >
                            <MatrixCellChip cell={row.cells?.[id]} />
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Legend */}
          {scopeIds.length > 0 && matrixRows.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-[11px] text-gray-600">
              <LegendItem className="bg-iflow-700 text-white" label="Edit" note="explicit grant" />
              <LegendItem
                className="bg-white text-iflow-700 border border-dashed border-iflow-300"
                label="Edit ↳"
                note="inherited from parent"
              />
              <LegendItem className="bg-purple-100 text-purple-700" label="Edit" note="group grant" />
              <LegendItem className="bg-iflow-900 text-white" label="Admin" note="owner / admin" />
              <LegendItem className="bg-red-100 text-red-700" label="⛔ Denied" note="deny override" />
              <LegendItem className="bg-amber-100 text-amber-800" label="View 🔒" note="capped by licence" />
            </div>
          )}
        </div>
      </div>

      {/* Explain panel + Grant form */}
      {(showExplain || showGrant) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {showExplain && (
            <div className="light-card p-4 lg:p-6">
              <div className="font-bold text-gray-800 mb-3">
                Explain access — why can this user reach these?
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Member
                  </label>
                  <select
                    value={explainMember}
                    onChange={(e) => setExplainMember(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="">Select a member…</option>
                    {memberRows.map((m) => (
                      <option key={m.member_id} value={m.member_id}>
                        {m.email ?? m.member_id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">
                    Resource
                  </label>
                  <select
                    value={explainResource}
                    onChange={(e) => setExplainResource(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="">Select a resource…</option>
                    {scopeIds.map((id) => (
                      <option key={id} value={id}>
                        {resourceNames[id] ?? id}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {!explainResource ? (
                <p className="text-sm text-gray-500">
                  Pick a member and a resource to trace their effective access.
                </p>
              ) : explainQuery.isLoading ? (
                <LoadingSkeleton rows={3} type="list" />
              ) : explainQuery.isError ? (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  Could not compute the access trace.
                </div>
              ) : explainQuery.data ? (
                <ExplainTraceStepper trace={explainQuery.data} />
              ) : null}
            </div>
          )}

          {showGrant && (
            <div className="light-card p-4 lg:p-6">
              <div className="font-bold text-gray-800 mb-3">Grant access</div>

              {/* Who */}
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Who
              </label>
              <div className="flex gap-2 mb-2">
                {(['user', 'group', 'external_org'] as GranteeType[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setGranteeType(t)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                      granteeType === t
                        ? 'bg-iflow-50 border-iflow-400 text-iflow-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {t === 'user' ? 'User' : t === 'group' ? 'Group' : 'External org'}
                  </button>
                ))}
              </div>
              {granteeType === 'user' && (
                <input
                  value={granteeEmail}
                  onChange={(e) => setGranteeEmail(e.target.value)}
                  placeholder="member@example.com"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-3"
                />
              )}
              {granteeType === 'group' && (
                <input
                  value={grantGroupId}
                  onChange={(e) => setGrantGroupId(e.target.value)}
                  placeholder="group id"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-3"
                />
              )}
              {granteeType === 'external_org' && (
                <input
                  value={grantExternalOrgId}
                  onChange={(e) => setGrantExternalOrgId(e.target.value)}
                  placeholder="external org id"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-3"
                />
              )}

              {/* To */}
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                To
              </label>
              <select
                value={grantResource}
                onChange={(e) => setGrantResource(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white mb-3"
              >
                <option value="">Select a resource…</option>
                {(scopeIds.length > 0 ? scopeIds : candidateIds.slice(0, 8)).map((id) => (
                  <option key={id} value={id}>
                    {resourceNames[id] ?? id}
                  </option>
                ))}
              </select>

              {/* Level */}
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Level
              </label>
              <div className="flex gap-2 mb-3">
                {([
                  ['view', 'View'],
                  ['edit', 'Edit'],
                  ['manager', 'Manage'],
                ] as [GrantLevel, string][]).map(([val, lbl]) => (
                  <button
                    key={val}
                    onClick={() => setGrantLevel(val)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${
                      grantLevel === val
                        ? 'bg-iflow-700 border-iflow-500 text-white'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>

              {/* Effect */}
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Effect
              </label>
              <div className="flex gap-2 mb-3">
                {([
                  ['allow', 'Allow'],
                  ['deny', 'Deny override'],
                ] as [Effect, string][]).map(([val, lbl]) => (
                  <button
                    key={val}
                    onClick={() => setGrantEffect(val)}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${
                      grantEffect === val
                        ? val === 'deny'
                          ? 'bg-red-100 border-red-300 text-red-700'
                          : 'bg-iflow-50 border-iflow-400 text-iflow-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>

              <div
                className={`rounded-lg px-4 py-3 text-sm mb-4 ${
                  grantEffect === 'deny'
                    ? 'bg-red-50 border border-red-200 text-red-700'
                    : 'bg-amber-50 border border-amber-200 text-amber-800'
                }`}
              >
                This {grantEffect === 'deny' ? 'denies' : 'allows'}{' '}
                <b>{granteeDisplay}</b> {LEVEL_LABEL[grantLevel] ?? grantLevel} on{' '}
                <b>{grantResourceDisplay}</b> and cascades to everything inside.
              </div>

              {grantMutation.isError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700 mb-3">
                  Failed to apply the grant. Check the details and try again.
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowGrant(false)}
                  className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => grantMutation.mutate()}
                  disabled={!grantValid || grantMutation.isPending}
                  className="bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {grantMutation.isPending
                    ? 'Applying…'
                    : `${grantEffect === 'deny' ? 'Deny' : 'Grant'} ${LEVEL_LABEL[grantLevel] ?? grantLevel}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cross-organisation panel */}
      <div className="light-card p-4 lg:p-6">
        <div className="font-bold text-gray-800 mb-1">
          Cross-organisation access — who controls what
        </div>
        <p className="text-xs text-gray-500 mb-4">
          External parties granted access into the selected scope.
        </p>

        {scopeIds.length === 0 ? (
          <p className="text-sm text-gray-500">
            Select a scope to see external grants on those resources.
          </p>
        ) : crossOrgQuery.isLoading ? (
          <LoadingSkeleton rows={2} type="table" />
        ) : crossOrgQuery.isError ? (
          // BUG-153: a load failure is NOT the same as "no external access".
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            Could not load cross-organisation grants for the selected scope. This does{' '}
            <b>not</b> mean there are none — please retry or narrow the scope.
          </div>
        ) : (crossOrgQuery.data?.grants.length ?? 0) === 0 ? (
          (crossOrgQuery.data?.failedCount ?? 0) > 0 ? (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              Some resources in scope could not be checked for external grants, and no external
              access was found on the rest. The picture may be incomplete — please retry.
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No external organisations currently have access to the selected scope.
            </p>
          )
        ) : (
          <div className="overflow-x-auto">
            {(crossOrgQuery.data?.failedCount ?? 0) > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-800 mb-3">
                Some resources in scope could not be checked — the list below may be incomplete.
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 border-b border-gray-200">External party</th>
                  <th className="px-3 py-2 border-b border-gray-200">Can reach</th>
                  <th className="px-3 py-2 border-b border-gray-200">Level</th>
                  <th className="px-3 py-2 border-b border-gray-200">Constraints</th>
                  <th className="px-3 py-2 border-b border-gray-200">Expires</th>
                </tr>
              </thead>
              <tbody>
                {(crossOrgQuery.data?.grants ?? []).map((g, i) => {
                  const constraints: string[] = []
                  if (g.allow_download === false) constraints.push('No download')
                  if (g.allow_print === false) constraints.push('No print')
                  if (g.allow_download === false || g.allow_print === false)
                    constraints.push('Watermark')
                  return (
                    <tr key={`${g.resource_id}-${g.external_org_id ?? i}`}>
                      <td className="px-3 py-2 border-b border-gray-100">
                        <span className="font-semibold text-gray-800">
                          {g.grantee_display_name ?? g.external_org_id ?? g.grantee_email ?? 'External org'}
                        </span>
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100 text-gray-600">
                        📄 {resourceNames[g.resource_id] ?? g.resource_id}
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100">
                        <span className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold bg-iflow-100 text-iflow-700">
                          {levelLabel(g.permission_level)}
                        </span>
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100">
                        {constraints.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {constraints.map((c) => (
                              <span
                                key={c}
                                className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-[11px]"
                              >
                                {c}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 border-b border-gray-100 text-gray-600">
                        {g.expires_at
                          ? shortDate(g.expires_at)
                          : 'No expiry'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div className="rounded-lg bg-iflow-50 border border-iflow-200 px-4 py-3 text-xs text-iflow-900">
            <b>When YOU own the resource (Org A):</b> you and your Org Admins
            decide who outside the org may access it, at what level (default
            View), with what constraints (no-download / no-print / watermark) and
            expiry — and you can revoke instantly. Your encryption key still
            governs the data.
          </div>
          <div className="rounded-lg bg-iflow-50 border border-iflow-200 px-4 py-3 text-xs text-iflow-900">
            <b>When THEY own the resource (Org B):</b> the owner sets the ceiling;
            your Org Admin decides which of <i>your</i> members may use the grant,
            and your members are billed on <i>your</i> seats. Effective access =
            the lower of the owner&apos;s grant and your member&apos;s licence.
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Analyst visibility editor (Fig56 headline feature)
// ---------------------------------------------------------------------------

function AnalystVisibilityPanel({
  analystOptions,
  analystsLoading,
  selectedAnalyst,
  onSelectAnalyst,
  candidateIds,
  resourceNames,
  onSaved,
}: {
  analystOptions: { id: string; label: string }[]
  analystsLoading: boolean
  selectedAnalyst: string
  onSelectAnalyst: (id: string) => void
  candidateIds: string[]
  resourceNames: Record<string, string>
  onSaved: () => void
}) {
  // hiddenIds tracks the projects this analyst CANNOT see (ticked = hidden).
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set())
  const [baselineLoaded, setBaselineLoaded] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  // Load the analyst's CURRENT hidden set so Save diffs against the real
  // baseline (never a blind clear). BACKEND NOTE: getAnalystProjectVisibility
  // may 404 until its GET route ships — that is treated as "could not load",
  // which keeps Save disabled (the safe failure mode: it can never silently
  // un-hide everything).
  const {
    data: visibility,
    isLoading: visibilityLoading,
    isError: visibilityError,
  } = useQuery({
    queryKey: ['analyst-project-visibility', selectedAnalyst],
    queryFn: () => getAnalystProjectVisibility(selectedAnalyst),
    enabled: !!selectedAnalyst,
  })

  // Reset baseline whenever the selected analyst changes, then seed from server.
  useEffect(() => {
    setBaselineLoaded(false)
    setHiddenIds(new Set())
    setSaveError(null)
  }, [selectedAnalyst])

  useEffect(() => {
    if (visibility && !baselineLoaded) {
      setHiddenIds(new Set(visibility.hidden_project_ids ?? []))
      setBaselineLoaded(true)
    }
  }, [visibility, baselineLoaded])

  const save = useMutation({
    mutationFn: () => setAnalystProjectVisibility(selectedAnalyst, Array.from(hiddenIds)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['analyst-project-visibility', selectedAnalyst] })
      onSaved()
    },
    onError: (err) =>
      setSaveError(errorMessage(err, 'Failed to save visibility. Please try again.')),
  })

  const toggle = (id: string) =>
    setHiddenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const baselineHidden = useMemo(
    () => new Set(visibility?.hidden_project_ids ?? []),
    [visibility?.hidden_project_ids],
  )
  const pendingChanges = useMemo(() => {
    let n = 0
    for (const id of candidateIds) {
      if (hiddenIds.has(id) !== baselineHidden.has(id)) n += 1
    }
    return n
  }, [candidateIds, hiddenIds, baselineHidden])

  const canSave = baselineLoaded && !save.isPending && pendingChanges > 0
  const visibleCount = candidateIds.filter((id) => !hiddenIds.has(id)).length

  return (
    <div className="light-card p-4 lg:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Eye size={18} className="text-iflow-600" />
          <div>
            <h2 className="font-bold text-gray-800">Analyst visibility</h2>
            <p className="text-xs text-gray-500">
              Hide whole projects from an Analyst-role member without changing anyone else's access.
            </p>
          </div>
        </div>
        <div className="min-w-[220px]">
          <label htmlFor="visibility-analyst" className="sr-only">
            Choose an analyst
          </label>
          <select
            id="visibility-analyst"
            value={selectedAnalyst}
            onChange={(e) => onSelectAnalyst(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-iflow-200"
          >
            <option value="">
              {analystsLoading
                ? 'Loading analysts…'
                : analystOptions.length === 0
                  ? 'No analysts in this org'
                  : 'Choose an analyst…'}
            </option>
            {analystOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedAnalyst ? (
        <div className="mt-4">
          {analystOptions.length === 0 && !analystsLoading ? (
            <EmptyState
              compact
              icon={Eye}
              title="No analysts yet"
              description="Invite a member with the Analyst role on Members & Licences, then choose them here to tailor which projects they can see."
            />
          ) : (
            <p className="text-sm text-gray-500 mt-2">
              Choose an analyst above to see the projects they can currently reach, then tick a
              project to hide it. Saving is <b>additive</b> — it never reopens projects hidden
              earlier.
            </p>
          )}
        </div>
      ) : visibilityLoading ? (
        <div className="mt-4">
          <LoadingSkeleton rows={4} type="list" />
        </div>
      ) : visibilityError ? (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          Could not load this analyst&apos;s current visibility. Saving is disabled to avoid
          accidentally clearing existing restrictions — please retry.
        </div>
      ) : candidateIds.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            compact
            icon={FolderTree}
            title="No projects to scope"
            description="There are no projects in this organisation to hide or reveal yet."
          />
        </div>
      ) : (
        <>
          <div className="mt-4 rounded-lg bg-green-50 border border-green-200 px-4 py-2.5 text-sm text-green-800">
            Loaded {baselineHidden.size} hidden project{baselineHidden.size === 1 ? '' : 's'} for
            this analyst from the server. Your existing restrictions are pre-ticked — Save keeps
            them.
          </div>
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-800">
            Ticking hides a project; <b>unticking</b> is the only way to reveal one. Saving is
            additive — it will not touch hides you don&apos;t change.
          </div>

          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mt-4 mb-2">
            Hide from this analyst (ticked = hidden) · {visibleCount} of {candidateIds.length}{' '}
            visible
          </p>
          <ul className="space-y-1 max-h-[360px] overflow-y-auto">
            {candidateIds.map((id) => {
              const hidden = hiddenIds.has(id)
              return (
                <li key={id}>
                  <label className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hidden}
                      onChange={() => toggle(id)}
                      className="w-4 h-4 rounded border-gray-300 text-iflow-600 focus:ring-iflow-500"
                    />
                    <span
                      className={`flex-1 text-sm ${
                        hidden ? 'text-gray-500 line-through' : 'text-gray-800'
                      }`}
                    >
                      {resourceNames[id] ?? id}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        hidden ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {hidden ? (
                        <>
                          <EyeOff size={11} /> Hidden
                        </>
                      ) : (
                        <>
                          <Eye size={11} /> Visible
                        </>
                      )}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>

          {saveError && (
            <div className="mt-3 rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
              {saveError}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-xs text-gray-500">
              {hiddenIds.size} hidden ·{' '}
              {pendingChanges === 0
                ? 'no changes pending'
                : `${pendingChanges} change${pendingChanges === 1 ? '' : 's'} pending`}
            </span>
            <button
              type="button"
              onClick={() => {
                setSaveError(null)
                if (canSave) save.mutate()
              }}
              disabled={!canSave}
              className="bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {save.isPending ? 'Saving…' : 'Save visibility'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function LegendItem({
  className,
  label,
  note,
}: {
  className: string
  label: string
  note: string
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold ${className}`}>
        {label}
      </span>
      {note}
    </span>
  )
}

function ExplainTraceStepper({ trace }: { trace: ExplainTrace }) {
  const steps: { title: string; detail: string; deny?: boolean }[] = []

  steps.push({
    title: trace.can_view
      ? `Can view${trace.can_edit ? ' and edit' : ''}`
      : 'Cannot reach this resource',
    detail: `Effective level: ${levelLabel(trace.effective_level)} · source: ${trace.source}`,
    deny: !trace.can_view,
  })

  if (trace.capped_by) {
    steps.push({
      title: `Capped by ${trace.capped_by}`,
      detail: `Their seat / policy limits the effective level to ${levelLabel(trace.effective_level)}.`,
    })
  }

  steps.push({
    title: 'Why',
    detail: trace.reason || 'Most-specific node wins; deny beats allow at the same depth.',
  })

  if (trace.node) {
    steps.push({
      title: 'Deciding node',
      detail: trace.node,
    })
  }

  return (
    <ol className="space-y-0">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div
              className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${
                s.deny ? 'bg-red-100 text-red-700' : 'bg-iflow-50 text-iflow-700'
              }`}
            >
              {i + 1}
            </div>
            {i < steps.length - 1 && <div className="w-0.5 flex-1 bg-iflow-100 my-1" />}
          </div>
          <div className="pb-4">
            <div className="font-semibold text-gray-800 text-sm flex items-center gap-2">
              {s.title}
              {s.deny && (
                <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[11px] font-medium">
                  Deny override
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">{s.detail}</div>
          </div>
        </li>
      ))}
    </ol>
  )
}
